import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from "@mui/lab";
import EventIcon from "@mui/icons-material/Event";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import LockIcon from "@mui/icons-material/Lock";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { formatDate } from "../../utils/dateFormat";
import { emptyScheduleDialogState } from "./patientScheduleUtils";

export { emptyScheduleDialogState };

export function ScheduleDateDialog({
  open,
  onClose,
  onSave,
  onSelectedDateChange,
  scheduling = false,
  scheduleDialog = emptyScheduleDialogState,
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <span>{scheduleDialog.label || "Set Scheduled Date"}</span>
        {scheduleDialog.scheduleType ? (
          <Typography component="span" variant="caption" sx={{ fontWeight: 800, color: "#6b7280" }}>
            ({scheduleDialog.scheduleType})
          </Typography>
        ) : null}
      </DialogTitle>
      <DialogContent>
        <DatePicker
          label="Scheduled Date"
          format="MM/DD/YYYY"
          value={scheduleDialog.selectedDate ? dayjs(scheduleDialog.selectedDate) : null}
          onChange={(value) =>
            onSelectedDateChange?.(value && value.isValid() ? value.format("YYYY-MM-DD") : "")
          }
          minDate={scheduleDialog.minDate ? dayjs(scheduleDialog.minDate) : undefined}
          maxDate={scheduleDialog.maxDate ? dayjs(scheduleDialog.maxDate) : undefined}
          slotProps={{
            textField: {
              fullWidth: true,
              helperText:
                scheduleDialog.minDate && scheduleDialog.maxDate
                  ? `Allowed range: ${formatDate(scheduleDialog.minDate)} to ${formatDate(scheduleDialog.maxDate)}`
                  : scheduleDialog.minDate
                    ? `Allowed from ${formatDate(scheduleDialog.minDate)}`
                    : "Any date allowed",
              sx: { mt: 1 },
            },
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={scheduling || !scheduleDialog.selectedDate}
        >
          {scheduling ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function mapPatientScheduleRows(schedules) {
  return [...(schedules || [])]
    .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
    .map((row) => ({
      id: row.id,
      execution_id: row.execution_id,
      schedule_id: row.id,
      schedule: row.order,
      type: row.schedule_type,
      status: row.status,
      display_status: row.display_status || "pending",
      can_schedule: Boolean(row.can_schedule),
      optimal_date: row.optimal_date,
      window_open: row.window_open_date,
      window_close: row.window_close_date,
      scheduled_date: row.actual_date,
    }));
}

const horizontalTimelineSx = {
  m: 0,
  p: 0,
  width: "max-content",
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-start",
  "& .MuiTimelineItem-root": {
    minHeight: "auto",
    flex: "0 0 300px",
    width: 300,
    maxWidth: 300,
    px: 0,
    alignItems: "stretch",
    "&:before": { display: "none" },
  },
  "& .MuiTimelineSeparator-root": {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    minHeight: "auto",
    mb: 1,
  },
  "& .MuiTimelineConnector-root": {
    flexGrow: 1,
    width: "auto",
    height: 2,
    bgcolor: "#90a4ae",
  },
  "& .MuiTimelineContent-root": {
    py: 0,
    px: 0,
    width: "100%",
    minWidth: 0,
    flex: "1 1 auto",
  },
};

const verticalTimelineSx = {
  m: 0,
  p: 0,
  width: "100%",
  "& .MuiTimelineItem-root": {
    minHeight: "auto",
    "&:before": { display: "none" },
  },
  "& .MuiTimelineContent-root": { py: 0, px: 0, flex: 1 },
};

function ScheduleCard({ schedule: s, onScheduleClick, onAnswerOnBehalf, schedules }) {
  const isDynamic = String(s.type || "").toLowerCase() === "dynamic";
  const displayStatus = String(s.display_status || "").toLowerCase();
  const isOutside = displayStatus === "outside_window";
  const isCompleted = displayStatus === "completed";
  const isScheduled = displayStatus === "scheduled";
  const statusColor = isOutside
    ? "#d32f2f"
    : isCompleted
      ? "#2e7d32"
      : isScheduled
        ? "#1976d2"
        : "#6b7280";
  const StatusIcon = isOutside ? ErrorOutlineIcon : isCompleted ? CheckCircleIcon : ScheduleIcon;

  return (
    <Box
      onClick={() => onScheduleClick?.(s, schedules)}
      sx={{
        border: onScheduleClick ? "1px solid #3f7fbf" : "1px solid #dce5ed",
        borderRadius: 2,
        p: { xs: 1.25, md: 1.5 },
        bgcolor: "#fff",
        cursor: onScheduleClick ? "pointer" : "default",
        width: "100%",
        boxSizing: "border-box",
        "&:hover": onScheduleClick
          ? {
              boxShadow: "0 2px 8px rgba(63, 127, 191, 0.15)",
            }
          : undefined,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 0.5,
          mb: 0.5,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, fontSize: { xs: "0.8rem", md: "0.875rem" } }}
        >
          Schedule {s.schedule} ({String(s.type || "").toUpperCase()})
        </Typography>
        {onAnswerOnBehalf ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AssignmentIcon sx={{ fontSize: 14, display: { xs: "none", sm: "inline-flex" } }} />}
            onClick={(event) => {
              event.stopPropagation();
              onAnswerOnBehalf(s, schedules);
            }}
            sx={{
              flexShrink: 0,
              textTransform: "none",
              fontSize: { xs: "0.65rem", md: "0.7rem" },
              py: 0.25,
              px: { xs: 0.75, md: 1 },
              minHeight: { xs: 24, md: 28 },
              borderColor: "#1565c0",
              color: "#1565c0",
              lineHeight: 1.2,
            }}
          >
            Answer on behalf
          </Button>
        ) : null}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: 1 }}>
        <StatusIcon sx={{ fontSize: 16, color: statusColor }} />
        <Typography variant="caption" sx={{ color: statusColor, fontWeight: 700 }}>
          {String(displayStatus || "").toUpperCase() || "-"}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, color: "#1976d2" }}>
          <LockOpenIcon sx={{ fontSize: 18, flexShrink: 0 }} />
          <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", md: "inherit" } }}>
            Window Open: {s.window_open == null ? "N/A" : formatDate(s.window_open)}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, color: "#2e7d32" }}>
          <GpsFixedIcon sx={{ fontSize: 18, flexShrink: 0 }} />
          <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", md: "inherit" } }}>
            Optimal Date: {s.optimal_date == null ? "N/A" : formatDate(s.optimal_date)}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, color: "#ed6c02" }}>
          <LockIcon sx={{ fontSize: 18, flexShrink: 0 }} />
          <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", md: "inherit" } }}>
            Window Close: {s.window_close == null ? "N/A" : formatDate(s.window_close)}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap" }}>
          <LinearProgress
            variant="determinate"
            value={s.scheduled_date ? 100 : 0}
            sx={{ height: 6, borderRadius: 999, width: 96, flexShrink: 0 }}
            color={s.scheduled_date ? "success" : "primary"}
          />
          <Typography variant="caption">
            Scheduled: {formatDate(s.scheduled_date)}
          </Typography>
        </Box>
        {onScheduleClick ? (
          <Typography variant="caption" sx={{ color: "#1565c0" }}>
            Click card to set scheduled date
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

function PatientSchedulePanel({
  schedules = [],
  loading = false,
  error = "",
  patientLabel,
  onScheduleClick,
  onAnswerOnBehalf,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3, width: "100%" }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" color="error" sx={{ py: 1 }}>
        {error}
      </Typography>
    );
  }

  if (!schedules.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        No schedules found for this subject.
      </Typography>
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, py: { xs: 1.5, md: 0 }, px: { xs: 0.5, md: 0 } }}>
      {isMobile ? (
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
          Schedules{patientLabel ? ` — ${patientLabel}` : ""}
        </Typography>
      ) : null}
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          overflowX: "auto",
          overflowY: isMobile ? "auto" : "hidden",
          WebkitOverflowScrolling: "touch",
          maxHeight: isMobile ? 480 : "none",
          pb: 1,
          borderRadius: { xs: 1, md: 0 },
          border: { xs: "1px solid #e3edf3", md: "none" },
          bgcolor: { xs: "#f8fbfd", md: "transparent" },
          px: { xs: 0.5, md: 0 },
          "&::-webkit-scrollbar": { height: 8, width: 8 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(63, 127, 191, 0.45)",
            borderRadius: 8,
          },
        }}
      >
        {isMobile ? (
          <Timeline sx={verticalTimelineSx}>
            {schedules.map((s, index) => {
              const isDynamic = String(s.type || "").toLowerCase() === "dynamic";
              return (
                <TimelineItem key={s.id}>
                  <TimelineSeparator>
                    <TimelineDot color={isDynamic ? "secondary" : "primary"}>
                      {isDynamic ? <AutorenewIcon fontSize="small" /> : <EventIcon fontSize="small" />}
                    </TimelineDot>
                    {index < schedules.length - 1 ? <TimelineConnector /> : null}
                  </TimelineSeparator>
                  <TimelineContent sx={{ pb: 2 }}>
                    <ScheduleCard
                      schedule={s}
                      onScheduleClick={onScheduleClick}
                      onAnswerOnBehalf={onAnswerOnBehalf}
                      schedules={schedules}
                    />
                  </TimelineContent>
                </TimelineItem>
              );
            })}
          </Timeline>
        ) : (
          <Timeline sx={horizontalTimelineSx}>
            {schedules.map((s, index) => {
              const isDynamic = String(s.type || "").toLowerCase() === "dynamic";
              return (
                <TimelineItem key={s.id}>
                  <TimelineSeparator>
                    {index > 0 ? <TimelineConnector /> : null}
                    <TimelineDot color={isDynamic ? "secondary" : "primary"}>
                      {isDynamic ? <AutorenewIcon fontSize="small" /> : <EventIcon fontSize="small" />}
                    </TimelineDot>
                    {index < schedules.length - 1 ? <TimelineConnector /> : null}
                  </TimelineSeparator>
                  <TimelineContent>
                    <ScheduleCard
                      schedule={s}
                      onScheduleClick={onScheduleClick}
                      onAnswerOnBehalf={onAnswerOnBehalf}
                      schedules={schedules}
                    />
                  </TimelineContent>
                </TimelineItem>
              );
            })}
          </Timeline>
        )}
      </Box>
    </Box>
  );
}

export default PatientSchedulePanel;
